import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  IconEyePlus,
  IconGripVertical,
  IconLoader2,
  IconMovie,
  IconPhotoFilled,
  IconFileTypePdf,
  IconUpload,
} from '@tabler/icons-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-toastify';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useTheme from 'chat-ui/src/hooks/useTheme';
import useImageViewerStore from 'chat-ui/src/store/ImageViewerStore';
import { QueryTag } from '@common/ts/enums';
import { MAX_UPLOAD_FILE_SIZE_BYTES } from '@common/media';
import { getErrorMessage } from '@common/utils/getErrorDetails';
import RequiredLabel from '@/components/labels/RequiredLabel';
import { Input } from '@/components/ui/input';
import useTranslation from '@/hooks/useTranslation';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import useCreateProjectStore from '@/store/CreateProjectStore';
import getTextColor from '@/utils/getTextColorContrast';
import { MediaUploadingProps, Media } from '@/types';
import { Button } from '@/components/ui/button';
import DeleteQuestionMediaModal from '@/components/Modals/DeleteQuestionMediaModal';
import { cn } from '@/lib/utils.ts';
import axiosInstance from '@/axios/axiosInstance';
import { API_ENDPOINTS } from '@/api-endpoints';

const MAX_MEDIA_FILES = 10;

interface MediaItemProps {
  media: Media;
  isDisabled: boolean;
  isUploading: boolean;
  onDelete: (mediaId: string, mediaName: string) => void;
  onPreview: (mediaUrl: string, mediaName: string) => void;
  onLabelChange: (mediaId: string, label: string) => void;
}

function MediaItem({
  media,
  isDisabled,
  isUploading,
  onDelete,
  onPreview,
  onLabelChange,
}: MediaItemProps) {
  const { lang } = useTranslation();
  const [localLabel, setLocalLabel] = useState(media.label || '');

  // Sync localLabel with media.label prop changes
  useEffect(() => {
    setLocalLabel(media.label || '');
  }, [media.label]);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: media._id,
    disabled: isDisabled || isUploading,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isPDF = media.type === 'application/pdf';
  const canPreview = !isPDF;
  const isImage = media.type?.startsWith('image/');

  const handleLabelChange = (newLabel: string) => {
    setLocalLabel(newLabel);
    // Call onLabelChange immediately to trigger form state update
    if (newLabel !== media.label) {
      onLabelChange(media._id, newLabel);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('flex flex-col gap-2 p-3 border rounded', isDragging && 'z-50')}
    >
      <div className="flex items-center gap-2">
        {/* Drag Handle */}
        {!isDisabled && !isUploading && (
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
            <IconGripVertical size={20} className="text-gray-400" />
          </div>
        )}

        {/* Media Preview Button with gradient background */}
        <button
          onClick={() => canPreview && onPreview(media.url, media.name)}
          type="button"
          className={cn(
            'relative flex items-center flex-1 min-w-0 gap-2 py-2 px-3 text-sm font-semibold text-gray-700 overflow-hidden rounded border border-gray-200 bg-white hover:bg-gray-50',
            canPreview ? 'cursor-pointer' : 'cursor-default',
          )}
        >
          {/* Background image with mask gradient for images only */}
          {isImage && media.url && (
            <div
              className="absolute right-0 top-0 bottom-0 w-32 bg-cover bg-center opacity-30"
              style={{
                backgroundImage: `url(${media.url})`,
                maskImage: 'linear-gradient(to right, transparent 0%, black 100%)',
                WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 100%)',
              }}
            />
          )}

          <div className="relative z-10 flex items-center gap-2 w-full">
            {(() => {
              if (media.type === 'video/mp4') {
                return <IconMovie size={20} className="min-w-5" />;
              }
              if (isPDF) {
                return <IconFileTypePdf size={20} className="min-w-5" />;
              }
              return <IconPhotoFilled size={20} className="min-w-5" />;
            })()}
            <span className="overflow-hidden truncate flex-1 text-left">{media.name}</span>
            {canPreview && <IconEyePlus size={20} className="text-gray-700" />}
          </div>
        </button>

        {/* Delete Button */}
        <Button
          variant="destructive"
          size="sm"
          onClick={() => onDelete(media._id, media.name)}
          disabled={isDisabled}
          type="button"
        >
          {lang.get('msg.delete')}
        </Button>
      </div>

      {/* Label Input */}
      <Input
        value={localLabel}
        onChange={(e) => handleLabelChange(e.target.value)}
        placeholder={
          lang.get('msg.customLabelForTheImageOptional') || 'Custom label for the image (optional)'
        }
        maxLength={50}
        disabled={isDisabled || isUploading}
        className="text-sm"
      />
    </div>
  );
}

// Media upload hook
function useMediaUpload(scope?: 'interview' | 'screening') {
  const queryClient = useQueryClient();
  const {
    organization_id: organizationId,
    workspace_id: workspaceId,
    project_id: projectId,
  } = useParams<{
    organization_id: string;
    workspace_id: string;
    project_id: string;
  }>();
  const { setUploadingQuestionId } = useCreateProjectStore();

  return useMutation({
    mutationFn: async ({ file, questionId }: { file: File; questionId: string }) => {
      const formData = new FormData();
      formData.append('media', file);

      const response = await axiosInstance.post(
        API_ENDPOINTS.PROJECTS.UPLOAD_MEDIA(
          organizationId,
          workspaceId,
          projectId,
          questionId,
          scope,
        ),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      return response.data;
    },
    onMutate: ({ questionId }) => {
      setUploadingQuestionId(questionId);
    },
    onSuccess: () => {
      // Clear uploading state BEFORE invalidating to avoid race conditions
      setUploadingQuestionId(null);

      // Invalidate with correct query key
      queryClient.invalidateQueries({
        queryKey: [QueryTag.SINGLE_PROJECT_DATA, projectId],
      });
    },
    onError: () => {
      // Also clear on error to ensure Save button isn't stuck disabled
      setUploadingQuestionId(null);
    },
  });
}

function QuestionMediaUploading({
  isProjectCompleted,
  isProjectPublished,
  registrationPath,
  setValue,
  currentQuestion,
  isQuestionSaved,
  scope,
}: MediaUploadingProps) {
  const { lang } = useTranslation();
  const { currentTheme } = useTheme();
  const { isUserViewer } = useWorkspaceRoles();
  const { uploadingQuestionId, setIsFormChanged } = useCreateProjectStore();
  const uploadMediaMutation = useMediaUpload(scope);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState<{ id: string; name: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setIsViewerOpen, setCurrentImageUrl, setCurrentImageAlt, setImages, setCurrentIndex } =
    useImageViewerStore();

  // Local state synced with currentQuestion.media
  const [mediaArray, setMediaArray] = useState<Media[]>(currentQuestion?.media || []);

  // Sync local state when currentQuestion.media changes, preserving unsaved local changes
  useEffect(() => {
    const incomingMedia = currentQuestion?.media || [];

    if (incomingMedia.length === 0) {
      setMediaArray([]);
      return;
    }

    // Build a map of current local labels to preserve unsaved changes
    const localLabelMap = new Map(mediaArray.map((m) => [m._id, m.label]));

    // Merge: use server data but preserve any labels that were set locally
    let hasLocalChanges = false;
    const mergedMedia = incomingMedia.map((serverMedia) => {
      const localLabel = localLabelMap.get(serverMedia._id);
      // If we have a local label and it's different from server, preserve it
      if (localLabel !== undefined && localLabel !== serverMedia.label) {
        hasLocalChanges = true;
        return { ...serverMedia, label: localLabel };
      }
      return serverMedia;
    });

    setMediaArray(mergedMedia);

    // CRITICAL: If we preserved local changes, update the form to maintain dirty state
    if (hasLocalChanges) {
      setValue(`${registrationPath}.media`, [...mergedMedia], {
        shouldDirty: true,
        shouldTouch: true,
      });
      setIsFormChanged(true);
    }
  }, [currentQuestion?.media]);

  const isThisQuestionUploading = uploadingQuestionId === currentQuestion?.id;
  const isAtMaxLimit = mediaArray.length >= MAX_MEDIA_FILES;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    if (mediaArray.length >= MAX_MEDIA_FILES) {
      toast.error(
        lang.get('msg.maximumMediaFilesReached') ||
          `Maximum ${MAX_MEDIA_FILES} media files allowed`,
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
      toast.error(lang.get('msg.maximum200MBfileError'));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (currentQuestion?.id) {
      try {
        await uploadMediaMutation.mutateAsync({
          file,
          questionId: currentQuestion.id,
        });

        toast.success(lang.get('msg.mediaUploadedSuccessfully') || 'Media uploaded successfully');

        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          lang.get('msg.uploadFailed') || 'Upload failed',
        );
        toast.error(errorMessage);
      }
    }
  };

  const isActionGloballyDisabled = isUserViewer || isProjectPublished || isProjectCompleted;
  const isFileUploadDisabled =
    isActionGloballyDisabled || isThisQuestionUploading || isAtMaxLimit || !isQuestionSaved;

  const showImagePreview = (imageURL: string, imageName: string) => {
    // Pass single image - viewer won't show carousel navigation for single images
    setImages([{ url: imageURL, label: imageName }]);
    setCurrentIndex(0);
    setCurrentImageUrl(imageURL);
    setCurrentImageAlt(imageName || 'image preview');
    setIsViewerOpen(true);
  };

  const onDeleteImage = (mediaId: string, mediaName: string) => {
    setMediaToDelete({ id: mediaId, name: mediaName });
    setOpenDeleteModal(true);
  };

  const handleDeleteSuccess = () => {
    setMediaToDelete(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = mediaArray.findIndex((m) => m._id === active.id);
      const newIndex = mediaArray.findIndex((m) => m._id === over.id);

      // Create a completely new array to ensure reference changes
      const newMediaArray = arrayMove([...mediaArray], oldIndex, newIndex);
      setMediaArray(newMediaArray);

      // Update form value with new array reference
      setValue(`${registrationPath}.media`, [...newMediaArray], {
        shouldDirty: true,
        shouldTouch: true,
      });

      // Mark form as changed to show Save button
      setIsFormChanged(true);
    }
  };

  const handleLabelChange = (mediaId: string, label: string) => {
    // Create a completely new array with new object references
    const updatedMediaArray = mediaArray.map((m) =>
      m._id === mediaId ? { ...m, label } : { ...m },
    );
    setMediaArray(updatedMediaArray);

    // Update form value with new array reference
    setValue(`${registrationPath}.media`, [...updatedMediaArray], {
      shouldDirty: true,
      shouldTouch: true,
    });

    // Mark form as changed to show Save button
    setIsFormChanged(true);
  };

  return (
    <div>
      <RequiredLabel notRequired title="uploadMedia" tooltip={lang.get('msg.uploadMediaInfo')}>
        <div className="flex flex-col items-center w-full gap-4">
          {/* Media Counter */}
          {mediaArray.length > 0 && (
            <div className="w-full text-sm font-semibold text-primary">
              {mediaArray.length} / {MAX_MEDIA_FILES} media files
            </div>
          )}

          {/* File Upload Input */}
          <div className="flex items-center w-full gap-1.5 cursor-pointer">
            <IconUpload
              size={25}
              style={{
                backgroundColor: currentTheme?.primary,
                color: getTextColor(currentTheme?.primary || ''),
              }}
              className="w-11 h-[38px] p-1.5 rounded-md"
            />
            <Input
              ref={fileInputRef}
              accept="image/jpeg, image/jpg, video/mp4, audio/mp3, audio/wav, image/png, image/webp, image/gif, application/pdf"
              onChange={handleFileChange}
              disabled={isFileUploadDisabled}
              type="file"
              className="w-full cursor-pointer"
            />
          </div>
        </div>
      </RequiredLabel>

      {/* Question Not Saved Warning */}
      {!isQuestionSaved && (
        <div className="mb-3 mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            {lang.get('msg.saveQuestionBeforeUpload') ||
              'Please save the question before uploading media.'}
          </p>
        </div>
      )}

      {/* Uploading State */}
      {isThisQuestionUploading && (
        <div className="flex items-center justify-center w-full gap-2 px-4 py-2 mt-3 border-2 border-dashed rounded-sm bg-primary/20 border-primary">
          <IconLoader2 size={20} className="animate-spin" />
          <p className="text-sm font-semibold text-primary">
            {lang.get('msg.uploading')}
            ...
          </p>
        </div>
      )}

      {/* Media List with Drag and Drop */}
      {!isThisQuestionUploading && mediaArray.length > 0 && (
        <div className="flex flex-col gap-3 mt-3">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={mediaArray.map((m) => m._id)}
              strategy={verticalListSortingStrategy}
            >
              {mediaArray.map((media) => (
                <MediaItem
                  key={media._id}
                  media={media}
                  isDisabled={isActionGloballyDisabled}
                  isUploading={isThisQuestionUploading}
                  onDelete={onDeleteImage}
                  onPreview={showImagePreview}
                  onLabelChange={handleLabelChange}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Delete Modal */}
      {mediaToDelete && (
        <DeleteQuestionMediaModal
          isOpen={openDeleteModal}
          onClose={() => {
            setOpenDeleteModal(false);
            setMediaToDelete(null);
          }}
          questionId={currentQuestion?.id}
          mediaName={mediaToDelete.name}
          onSuccess={handleDeleteSuccess}
          mediaId={mediaToDelete.id}
          scope={scope}
        />
      )}
    </div>
  );
}

export default QuestionMediaUploading;
